import type { ReactElement } from "react";
import type { LucideIcon } from "lucide-react";
import { AlertCircle } from "lucide-react";

export interface ErrorStateProps {
  readonly title?: string;
  readonly message: string;
  readonly icon?: LucideIcon;
  readonly className?: string;
}

/**
 * Generic error state display for full-page errors
 * Shows centered error message with optional icon and title
 *
 * @param title - Optional error title (default: "Error")
 * @param message - Error message to display
 * @param icon - Optional Lucide icon component (default: AlertCircle)
 * @param className - Optional additional CSS classes
 *
 * @example
 * ```tsx
 * <ErrorState message="Failed to load content" />
 * <ErrorState title="Not Found" message="Page not found" icon={Search} />
 * ```
 */
export function ErrorState({
  title = "Error",
  message,
  icon: Icon = AlertCircle,
  className = "",
}: ErrorStateProps): ReactElement {
  return (
    <div className={`bg-black h-screen flex items-center justify-center ${className}`}>
      <div className="flex flex-col items-center text-center max-w-md px-4">
        <Icon className="w-16 h-16 text-red-400 mb-4" />
        {title && <h2 className="text-2xl font-semibold text-white mb-2">{title}</h2>}
        <p className="text-red-400 text-xl">{message}</p>
      </div>
    </div>
  );
}
